import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/database.types'

export type LessonStatus = 'scheduled' | 'completed' | 'cancelled'

export interface Lesson {
  id: string
  scheduledAt: string
  finishedAt: string | null
  durationMinutes: number | null
  status: LessonStatus
  notes: string | null
  studentInitial: string | null
  instructorInitial: string | null
  createdAt: string
}

interface CreateLessonResult {
  ok: boolean
  code?: string
  lessonId?: string
}

interface UseLessonsReturn {
  lessons: Lesson[]
  loading: boolean
  error: string | null
  saving: boolean
  createLesson: (scheduledAt: string, finishedAt: string, notes: string | null) => Promise<CreateLessonResult>
  startLesson: () => Promise<CreateLessonResult>
  endLesson: (lessonId: string, finishedAt?: string) => Promise<{ ok: boolean; code?: string; durationMinutes?: number }>
  updateLesson: (lessonId: string, fields: Partial<Pick<Lesson, 'scheduledAt' | 'notes' | 'status' | 'studentInitial' | 'instructorInitial'>>) => Promise<{ ok: boolean }>
  refresh: () => void
}

/**
 * Lesson history and management for one instructor-student relationship. Authorization for
 * creation is re-verified entirely server-side inside create_lesson() (migrations 056, 071)
 * from relationshipId alone — this hook never asserts ownership itself. Reads/updates of
 * existing lessons rely on driving_lessons' own RLS (instructor_id = auth.uid()).
 *
 * create_lesson() takes a start AND finish time (migration 071 — matches the physical
 * CARS Learning Form A's own Start time/Finish time fields, which has no duration field at
 * all) and derives duration_minutes server-side; this hook never computes or sends a
 * duration directly.
 */
export function useLessons(relationshipId: string | undefined): UseLessonsReturn {
  const { user } = useAuth()
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchLessons = useCallback(async () => {
    if (!user || !relationshipId) return
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchErr } = await supabase
        .from('driving_lessons')
        .select('id, scheduled_at, finished_at, duration_minutes, status, notes, student_initial, instructor_initial, created_at')
        .eq('relationship_id', relationshipId)
        .order('scheduled_at', { ascending: true })

      if (fetchErr) { setError('Could not load lesson history.'); return }

      setLessons((data ?? []).map((row: any) => ({
        id: row.id,
        scheduledAt: row.scheduled_at,
        finishedAt: row.finished_at,
        durationMinutes: row.duration_minutes,
        status: row.status,
        notes: row.notes,
        studentInitial: row.student_initial,
        instructorInitial: row.instructor_initial,
        createdAt: row.created_at,
      })))
    } finally {
      setLoading(false)
    }
  }, [user, relationshipId])

  useEffect(() => { fetchLessons() }, [fetchLessons])

  const createLesson = useCallback(async (
    scheduledAt: string,
    finishedAt: string,
    notes: string | null,
  ): Promise<CreateLessonResult> => {
    if (!relationshipId) return { ok: false, code: 'no_relationship' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('create_lesson', {
        p_relationship_id: relationshipId,
        p_scheduled_at: scheduledAt,
        p_finished_at: finishedAt,
        p_notes: notes ?? undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string; lesson_id?: string } | null
      if (result?.ok) await fetchLessons()
      return { ok: result?.ok ?? false, code: result?.code, lessonId: result?.lesson_id }
    } finally {
      setSaving(false)
    }
  }, [relationshipId, fetchLessons])

  const startLesson = useCallback(async (): Promise<CreateLessonResult> => {
    if (!relationshipId) return { ok: false, code: 'no_relationship' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('start_lesson', {
        p_relationship_id: relationshipId,
        p_notes: undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string; lesson_id?: string } | null
      if (result?.ok) await fetchLessons()
      return { ok: result?.ok ?? false, code: result?.code, lessonId: result?.lesson_id }
    } finally {
      setSaving(false)
    }
  }, [relationshipId, fetchLessons])

  const endLesson = useCallback(async (lessonId: string, finishedAt?: string) => {
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('end_lesson', {
        p_lesson_id: lessonId,
        p_finished_at: finishedAt ?? undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string; duration_minutes?: number } | null
      if (result?.ok) await fetchLessons()
      return { ok: result?.ok ?? false, code: result?.code, durationMinutes: result?.duration_minutes }
    } finally {
      setSaving(false)
    }
  }, [fetchLessons])

  const updateLesson = useCallback(async (
    lessonId: string,
    fields: Partial<Pick<Lesson, 'scheduledAt' | 'notes' | 'status' | 'studentInitial' | 'instructorInitial'>>,
  ): Promise<{ ok: boolean }> => {
    setSaving(true)
    try {
      const patch: Database['public']['Tables']['driving_lessons']['Update'] = {}
      if (fields.scheduledAt !== undefined) patch.scheduled_at = fields.scheduledAt
      if (fields.notes !== undefined) patch.notes = fields.notes
      if (fields.status !== undefined) patch.status = fields.status
      if (fields.studentInitial !== undefined) patch.student_initial = fields.studentInitial
      if (fields.instructorInitial !== undefined) patch.instructor_initial = fields.instructorInitial

      const { error: updateErr } = await supabase
        .from('driving_lessons')
        .update(patch)
        .eq('id', lessonId)

      if (updateErr) return { ok: false }
      await fetchLessons()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [fetchLessons])

  return { lessons, loading, error, saving, createLesson, startLesson, endLesson, updateLesson, refresh: fetchLessons }
}
